import React, { useEffect, useState, Fragment } from 'react';
import { motion, Reorder, AnimatePresence } from 'framer-motion';
import { Driver, RouteStop } from '../data/mockData';
import { ChevronLeft, Check, Trash2, Edit2, Plus, GripVertical } from 'lucide-react';
interface ReroutePanelProps {
  driver: Driver;
  onClose: () => void;
  onSave?: (updatedStops: RouteStop[]) => void;
  onPreview?: (stops: RouteStop[]) => void;
  onCancel?: () => void;
}
function InsertZone({
  onInsert
}: {
  onInsert: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return <div className="relative h-0 flex items-center justify-center z-10" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} data-id="element-545">
      {/* Invisible hover target area */}
      <div className="absolute -top-[6px] -bottom-[6px] left-0 right-0 z-10 cursor-pointer" onClick={onInsert} data-id="element-546" />
      <AnimatePresence data-id="element-547">
        {hovered && <motion.button initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 20,
        stiffness: 400
      }} onClick={e => {
        e.stopPropagation();
        onInsert();
      }} className="absolute z-20 w-5 h-5 rounded-full bg-brand-blue hover:bg-brand-blue/90 flex items-center justify-center shadow-lg shadow-brand-blue/30 border border-brand-blue/50" data-id="element-548">
            <Plus className="w-3 h-3 text-white" strokeWidth={3} data-id="element-549" />
          </motion.button>}
      </AnimatePresence>
    </div>;
}
export function ReroutePanel({
  driver,
  onClose,
  onSave,
  onPreview,
  onCancel
}: ReroutePanelProps) {
  const [stops, setStops] = useState<RouteStop[]>(driver.route);
  const [editingStopId, setEditingStopId] = useState<string | null>(null);
  const [editAddress, setEditAddress] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  // Live preview: push changes to map whenever stops change
  useEffect(() => {
    if (hasChanges) {
      onPreview?.(stops);
    }
  }, [stops, hasChanges]); // eslint-disable-line react-hooks/exhaustive-deps
  const completedStops = stops.filter(s => s.status === 'completed');
  const pendingStops = stops.filter(s => s.status !== 'completed');
  const handleRemove = (id: string) => {
    setStops(stops.filter(s => s.id !== id));
    setHasChanges(true);
  };
  const handleEditStart = (stop: RouteStop) => {
    setEditingStopId(stop.id);
    setEditAddress(stop.address);
  };
  const handleEditSave = (id: string) => {
    setStops(stops.map(s => s.id === id ? {
      ...s,
      address: editAddress
    } : s));
    setEditingStopId(null);
    setHasChanges(true);
  };
  const handleInsertAt = (indexInFull: number) => {
    const newStop: RouteStop = {
      id: `new-${Date.now()}`,
      address: '',
      eta: 'TBD',
      window: 'N/A',
      status: 'pending',
      distance: '0 mi'
    };
    const newStops = [...stops];
    newStops.splice(indexInFull, 0, newStop);
    setStops(newStops);
    setEditingStopId(newStop.id);
    setEditAddress('');
    setHasChanges(true);
  };
  const handleAddStop = () => {
    handleInsertAt(stops.length);
  };
  const handleReorder = (newPending: RouteStop[]) => {
    setStops([...completedStops, ...newPending]);
    setHasChanges(true);
  };
  const handleCancel = () => {
    onCancel?.();
    onClose();
  };
  const handleSave = () => {
    onSave?.(stops);
    onClose();
  };
  const renderStopCard = (stop: RouteStop, idx: number, opts: {
    isDraggable?: boolean;
  } = {}) => {
    const isCompleted = stop.status === 'completed';
    const isEditing = editingStopId === stop.id;
    const isNew = stop.id.startsWith('new-');
    return <div className="flex items-stretch gap-1.5" data-id="element-550">
        {/* Main Card */}
        <div className={`flex-1 min-w-0 border p-2.5 transition-colors ${isCompleted ? 'bg-slate-850/30 border-slate-800/50' : 'bg-slate-900/80 border-slate-700'}`} data-id="element-551">
          <div className="flex items-start gap-2.5" data-id="element-552">
            {/* Drag handle + Status Icon */}
            <div className="mt-0.5 shrink-0 flex items-center gap-1" data-id="element-553">
              {opts.isDraggable && !isEditing && <GripVertical className="w-3.5 h-3.5 text-slate-600 cursor-grab active:cursor-grabbing" data-id="element-554" />}
              {isCompleted ? <div className="w-5 h-5 rounded-full bg-brand-green/20 flex items-center justify-center" data-id="element-555">
                  <Check className="w-3 h-3 text-brand-green" strokeWidth={3} data-id="element-556" />
                </div> : <div className="w-5 h-5 rounded-full border-2 border-slate-600 flex items-center justify-center bg-slate-900 text-[10px] font-bold text-slate-400" data-id="element-557">
                  {idx + 1}
                </div>}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0" data-id="element-558">
              {isNew && <div className="mb-1.5" data-id="element-559">
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border text-brand-blue border-brand-blue/30 bg-brand-blue/10" data-id="element-560">
                    ADDED BY DISPATCH
                  </span>
                </div>}

              {isEditing ? <div className="flex flex-col gap-2" data-id="element-561">
                  <input type="text" value={editAddress} onChange={e => setEditAddress(e.target.value)} placeholder="Enter address..." className="w-full bg-slate-950 border border-slate-600 rounded-md px-2 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-brand-blue" autoFocus onKeyDown={e => {
                if (e.key === 'Enter') handleEditSave(stop.id);
                if (e.key === 'Escape') setEditingStopId(null);
              }} data-id="element-562" />
                  <div className="flex justify-end gap-2" data-id="element-563">
                    <button onClick={() => setEditingStopId(null)} className="text-[10px] font-medium text-slate-400 hover:text-slate-300 px-2 py-1" data-id="element-564">
                      Cancel
                    </button>
                    <button onClick={() => handleEditSave(stop.id)} className="text-[10px] font-medium bg-brand-blue hover:bg-brand-blue/90 text-white px-3 py-1 rounded" data-id="element-565">
                      Save
                    </button>
                  </div>
                </div> : <>
                  <div className={!isCompleted ? 'mb-1' : ''} data-id="element-566">
                    <p className={`text-sm font-medium leading-snug ${isCompleted ? 'text-slate-500' : 'text-slate-200'}`} data-id="element-567">
                      {stop.address || <span className="text-slate-500 italic" data-id="element-568">
                          No address set
                        </span>}
                    </p>
                    {stop.address && <p className={`text-[11px] ${isCompleted ? 'text-slate-600' : 'text-slate-400'}`} data-id="element-569">
                        Richmond, VA
                      </p>}
                  </div>
                </>}
            </div>
          </div>
        </div>

        {/* Action icons outside the card */}
        {!isCompleted && !isEditing ? <div className="flex flex-col items-center justify-center shrink-0 w-6" data-id="element-570">
            <button onClick={() => handleEditStart(stop)} className="flex-1 w-6 flex items-center justify-center rounded-t hover:bg-slate-800 text-slate-500 hover:text-slate-300 transition-colors" data-id="element-571">
              <Edit2 className="w-3 h-3" data-id="element-572" />
            </button>
            <button onClick={() => handleRemove(stop.id)} className="flex-1 w-6 flex items-center justify-center rounded-b hover:bg-brand-red/20 text-slate-500 hover:text-brand-red transition-colors" data-id="element-573">
              <Trash2 className="w-3 h-3" data-id="element-574" />
            </button>
          </div> : <div className="w-6 shrink-0" data-id="element-575" />}
      </div>;
  };
  return <motion.div initial={{
    x: '100%'
  }} animate={{
    x: 0
  }} exit={{
    x: '100%'
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 250
  }} className="absolute inset-0 bg-slate-950/95 backdrop-blur-md border-l border-slate-700 flex flex-col z-50 shadow-2xl" data-id="element-576">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-800 shrink-0 bg-slate-900/50" data-id="element-577">
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition-colors -ml-2" data-id="element-578">
          <ChevronLeft className="w-5 h-5" data-id="element-579" />
        </button>
        <h3 className="text-sm font-semibold text-slate-100" data-id="element-580">Edit Stops</h3>
        <span className="text-xs text-slate-500 ml-auto font-medium" data-id="element-581">
          {stops.length} stops
        </span>
      </div>

      {/* Stops List */}
      <div className="flex-1 overflow-y-auto px-4 py-3 scrollbar-hide" data-id="element-582">
        {/* Completed stops (static, not draggable) */}
        {completedStops.map((stop, idx) => {
        const fullIdx = idx;
        return <Fragment key={stop.id} data-id="element-583">
              {renderStopCard(stop, fullIdx)}
              <InsertZone onInsert={() => handleInsertAt(fullIdx + 1)} data-id="element-584" />
            </Fragment>;
      })}

        {/* Pending stops (draggable) */}
        <Reorder.Group axis="y" values={pendingStops} onReorder={handleReorder} className="mt-2.5 list-none flex flex-col p-0 m-0 gap-1" data-id="element-585">
          {pendingStops.map((stop, idx) => {
          const displayIdx = completedStops.length + idx;
          const fullIdx = completedStops.length + idx;
          return <Fragment key={stop.id} data-id="element-586">
                <Reorder.Item value={stop} dragListener={editingStopId !== stop.id} className="list-none gap-1" whileDrag={{
              scale: 1.02,
              boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
              zIndex: 50
            }} data-id="element-587">
                  {renderStopCard(stop, displayIdx, {
                isDraggable: true
              })}
                </Reorder.Item>
                {idx < pendingStops.length - 1 && <InsertZone onInsert={() => handleInsertAt(fullIdx + 1)} data-id="element-588" />}
              </Fragment>;
        })}
        </Reorder.Group>

        <button onClick={handleAddStop} className="w-full py-3 border-2 border-dashed border-slate-700 rounded-lg flex items-center justify-center gap-2 text-sm font-medium text-slate-400 hover:text-slate-200 hover:border-slate-500 hover:bg-slate-800/50 transition-all mt-3" data-id="element-589">
          <Plus className="w-4 h-4" data-id="element-590" />
          Add Stop
        </button>
      </div>

      {/* Save/Cancel Bar */}
      <div className="p-4 border-t border-slate-800 bg-slate-900/95 shrink-0 flex gap-3" data-id="element-591">
        <button onClick={handleCancel} className="flex-1 py-2 text-xs font-semibold rounded-lg transition-colors bg-slate-800 hover:bg-slate-700 text-slate-200" data-id="element-592">
          Cancel
        </button>
        <button onClick={handleSave} disabled={!hasChanges} className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${hasChanges ? 'bg-brand-blue hover:bg-brand-blue/90 text-white' : 'bg-slate-800/50 text-slate-500 cursor-default'}`} data-id="element-593">
          Apply Updates
        </button>
      </div>
    </motion.div>;
}